import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/types";
import type { CurrentUser } from "@/api/models";
import { isRole, toDepartment } from "@/api/models";

export function useCurrentUser() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const raw = await api.get<Record<string, unknown>>("/auth/me");
      const user = raw as components["schemas"]["UserRead"] & {
        department?: components["schemas"]["DepartmentRead"] & {
          logo_url?: string | null;
          // Not on DepartmentRead: /auth/me hand-builds its department payload
          // and adds this. It is the viewer's tenant, so it must not gate the
          // report's demonstration banner — the report carries that (RES-402).
          environment_class?: string | null;
        };
      };
      let department = user.department ?? null;
      if (!department && user.department_id) {
        const depts = await api.get<components["schemas"]["DepartmentRead"][]>(
          "/departments",
        );
        department = depts[0] ?? null;
      }
      const role = isRole(user.role) ? user.role : "firefighter";
      const roles =
        Array.isArray(user.roles) &&
        user.roles.some((candidate) => isRole(String(candidate)))
          ? user.roles.filter((candidate): candidate is CurrentUser["role"] =>
              isRole(String(candidate)),
            )
          : [role];
      return {
        ...user,
        role,
        roles,
        display_name: user.full_name || user.email,
        department: department ? toDepartment(department) : null,
        home_department_id:
          typeof raw.home_department_id === "string"
            ? raw.home_department_id
            : null,
      } satisfies CurrentUser;
    },
    staleTime: 5 * 60 * 1000,
  });
}
